import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { adminService } from '../../services/adminApi';
import { GlassButton, GlassCard, GlassInput, GlassPanel, GlassSelect, LoadingSpinner } from '../../components/GlassUI';
import {
  FaMoneyBillWave, FaPlus, FaTimes, FaCheck, FaExclamationTriangle,
  FaSearch, FaTrash, FaRupeeSign, FaCalendarAlt,
} from 'react-icons/fa';

interface Account {
  id: string;
  name: string;
  email: string;
  role: string;
  isActive: boolean;
  subscription?: { plan?: string; status?: string } | null;
}

interface Payment {
  id: string;
  userId: string;
  amount: number;
  plan?: string | null;
  method?: string | null;
  note?: string | null;
  paidAt: string;
  recordedBy?: string | null;
  account?: Account | null;
}

const METHODS = ['Cash', 'UPI', 'Card', 'Bank Transfer', 'Cheque', 'Other'];

const money = (n: number) =>
  '₹' + new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(Number(n) || 0);

const dateStr = (d: string) => {
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? '—' : dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const todayInput = () => new Date().toISOString().slice(0, 10);

const AdminPaymentsPage: React.FC = () => {
  const [payments, setPayments] = useState<Payment[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [totalCollected, setTotalCollected] = useState(0);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);
  const [search, setSearch] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [plans, setPlans] = useState<{ id: string; name: string }[]>([]);

  const [showCreate, setShowCreate] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    userId: '',
    amount: '',
    plan: '',
    method: 'Cash',
    paidAt: todayInput(),
    note: '',
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [data, catalog] = await Promise.all([
        adminService.listPayments(),
        adminService.listPlans().catch(() => ({ plans: [] })),
      ]);
      setPayments(data.payments || []);
      setAccounts(data.accounts || []);
      setTotalCollected(data.totalCollected || 0);
      setPlans((catalog.plans || []).filter((p: any) => p.id));
    } catch (err: any) {
      setMsg({ type: 'err', text: err.response?.data?.error || 'Failed to load payments' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const accountsById = useMemo(() => {
    const map: Record<string, Account> = {};
    for (const a of accounts) map[a.id] = a;
    return map;
  }, [accounts]);

  const monthTotal = useMemo(() => {
    const now = new Date();
    return payments
      .filter((p) => {
        const d = new Date(p.paidAt);
        return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
      })
      .reduce((sum, p) => sum + p.amount, 0);
  }, [payments]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return payments;
    return payments.filter((p) => {
      const a = p.account || accountsById[p.userId];
      return (
        (a?.name || '').toLowerCase().includes(q) ||
        (a?.email || '').toLowerCase().includes(q) ||
        (p.method || '').toLowerCase().includes(q) ||
        (p.note || '').toLowerCase().includes(q)
      );
    });
  }, [payments, search, accountsById]);

  const selectedAccount = form.userId ? accountsById[form.userId] : null;

  const openCreate = () => {
    setForm({
      userId: accounts[0]?.id || '',
      amount: '',
      plan: accounts[0]?.subscription?.plan || '',
      method: 'Cash',
      paidAt: todayInput(),
      note: '',
    });
    setShowCreate(true);
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.userId) {
      setMsg({ type: 'err', text: 'Select an account' });
      return;
    }
    if (!(Number(form.amount) > 0)) {
      setMsg({ type: 'err', text: 'Enter an amount greater than zero' });
      return;
    }
    setSaving(true);
    try {
      const res = await adminService.createPayment({
        userId: form.userId,
        amount: Number(form.amount),
        plan: form.plan || null,
        method: form.method || null,
        paidAt: form.paidAt,
        note: form.note || null,
      });
      setMsg({ type: 'ok', text: res.message || 'Payment recorded' });
      setShowCreate(false);
      load();
    } catch (err: any) {
      setMsg({ type: 'err', text: err.response?.data?.error || 'Failed to record payment' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (p: Payment) => {
    if (!window.confirm(`Remove the ${money(p.amount)} payment for ${p.account?.name || 'this account'}?`)) return;
    setBusyId(p.id);
    try {
      await adminService.deletePayment(p.userId, p.id);
      setMsg({ type: 'ok', text: 'Payment removed' });
      load();
    } catch (err: any) {
      setMsg({ type: 'err', text: err.response?.data?.error || 'Failed to remove payment' });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className='space-y-5'>
      {/* Header */}
      <div className='flex flex-col sm:flex-row sm:items-center gap-3'>
        <div className='flex-1'>
          <h1 className='text-2xl md:text-3xl font-bold text-slate-900 flex items-center gap-3'>
            <span className='w-10 h-10 rounded-xl bg-slate-900 text-amber-400 flex items-center justify-center'>
              <FaMoneyBillWave />
            </span>
            Payments
          </h1>
          <p className='text-slate-500 mt-1 text-sm'>
            Record every subscription payment you receive and keep a running ledger per account.
          </p>
        </div>
        <GlassButton variant='gold' onClick={openCreate} className='w-full sm:w-auto'>
          <FaPlus /> Record Payment
        </GlassButton>
      </div>

      {msg && (
        <div
          className={`flex items-start gap-2 text-sm rounded-2xl px-4 py-3 border ${
            msg.type === 'ok' ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-red-50 border-red-200 text-red-700'
          }`}
        >
          {msg.type === 'ok' ? <FaCheck className='mt-0.5 shrink-0' /> : <FaExclamationTriangle className='mt-0.5 shrink-0' />}
          <span className='break-all'>{msg.text}</span>
          <button className='ml-auto text-xs' onClick={() => setMsg(null)}>
            <FaTimes />
          </button>
        </div>
      )}

      {/* Summary */}
      <div className='grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4'>
        <GlassCard className='p-4'>
          <p className='text-xs text-slate-500 font-semibold uppercase tracking-wide'>Total collected</p>
          <p className='text-2xl font-bold text-emerald-600 mt-1'>{money(totalCollected)}</p>
        </GlassCard>
        <GlassCard className='p-4'>
          <p className='text-xs text-slate-500 font-semibold uppercase tracking-wide'>Collected this month</p>
          <p className='text-2xl font-bold text-amber-600 mt-1'>{money(monthTotal)}</p>
        </GlassCard>
        <GlassCard className='p-4'>
          <p className='text-xs text-slate-500 font-semibold uppercase tracking-wide'>Payments recorded</p>
          <p className='text-2xl font-bold text-slate-900 mt-1'>{payments.length}</p>
        </GlassCard>
      </div>

      {/* Search */}
      <div className='relative max-w-md'>
        <FaSearch className='absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 text-sm' />
        <GlassInput
          placeholder='Search by account, method or note...'
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className='!pl-11'
        />
      </div>

      {/* Ledger */}
      {loading ? (
        <LoadingSpinner label='Loading payments...' className='py-20' />
      ) : (
        <>
          {/* Desktop table */}
          <GlassPanel className='p-4 hidden md:block overflow-x-auto'>
            <table className='w-full text-sm text-slate-700'>
              <thead>
                <tr className='border-b border-slate-200 text-left'>
                  <th className='py-3 px-3 font-semibold'>Date</th>
                  <th className='py-3 px-3 font-semibold'>Account</th>
                  <th className='py-3 px-3 font-semibold'>Plan</th>
                  <th className='py-3 px-3 font-semibold'>Method</th>
                  <th className='py-3 px-3 font-semibold'>Note</th>
                  <th className='py-3 px-3 font-semibold text-right'>Amount</th>
                  <th className='py-3 px-3 font-semibold text-right'>Action</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => {
                  const a = p.account || accountsById[p.userId];
                  return (
                    <tr key={p.id} className='border-b border-slate-100 hover:bg-slate-50'>
                      <td className='py-3 px-3 whitespace-nowrap text-slate-600'>{dateStr(p.paidAt)}</td>
                      <td className='py-3 px-3'>
                        <p className='font-semibold text-slate-900'>{a?.name || 'Unknown'}</p>
                        <p className='text-xs text-slate-500'>{a?.email || ''}</p>
                      </td>
                      <td className='py-3 px-3 text-slate-600'>{p.plan || a?.subscription?.plan || '—'}</td>
                      <td className='py-3 px-3 text-slate-600'>{p.method || '—'}</td>
                      <td className='py-3 px-3 text-slate-500 text-xs max-w-[220px] truncate' title={p.note || ''}>
                        {p.note || '—'}
                      </td>
                      <td className='py-3 px-3 text-right font-bold text-emerald-600 whitespace-nowrap'>{money(p.amount)}</td>
                      <td className='py-3 px-3 text-right'>
                        <button
                          onClick={() => handleDelete(p)}
                          disabled={busyId === p.id}
                          className='text-red-500 hover:text-red-700 p-1.5 disabled:opacity-40'
                          title='Delete payment'
                        >
                          <FaTrash size={13} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {filtered.length === 0 && <p className='text-center text-slate-400 py-10'>No payments recorded yet</p>}
          </GlassPanel>

          {/* Mobile cards */}
          <div className='md:hidden space-y-3'>
            {filtered.map((p) => {
              const a = p.account || accountsById[p.userId];
              return (
                <GlassCard key={p.id} className='p-4'>
                  <div className='flex items-start justify-between gap-3'>
                    <div className='min-w-0'>
                      <p className='font-semibold text-slate-900 truncate'>{a?.name || 'Unknown'}</p>
                      <p className='text-xs text-slate-500 truncate'>{a?.email || ''}</p>
                      <p className='text-xs text-slate-400 mt-1 flex items-center gap-1'>
                        <FaCalendarAlt size={10} /> {dateStr(p.paidAt)} · {p.method || '—'}
                        {p.plan ? ` · ${p.plan}` : ''}
                      </p>
                      {p.note && <p className='text-xs text-slate-500 mt-1'>{p.note}</p>}
                    </div>
                    <div className='text-right shrink-0'>
                      <p className='font-bold text-emerald-600'>{money(p.amount)}</p>
                        <button
                          onClick={() => handleDelete(p)}
                          disabled={busyId === p.id}
                          className='text-red-500 hover:text-red-700 p-1.5 disabled:opacity-40'
                          title='Delete payment'
                        >
                        <FaTrash size={12} />
                      </button>
                    </div>
                  </div>
                </GlassCard>
              );
            })}
            {filtered.length === 0 && (
              <GlassCard className='text-center py-10 text-slate-400 text-sm'>No payments recorded yet</GlassCard>
            )}
          </div>
        </>
      )}

      {/* ── Record payment modal ── */}
      {showCreate && (
        <div className='fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4'>
          <div className='absolute inset-0 bg-slate-900/60 backdrop-blur-sm' onClick={() => setShowCreate(false)} />
          <div className='relative bg-white w-full sm:max-w-lg rounded-t-3xl sm:rounded-3xl p-6 max-h-[92vh] overflow-y-auto shadow-2xl'>
            <div className='flex items-center justify-between mb-1'>
              <h2 className='text-xl font-bold text-slate-900 flex items-center gap-2'>
                <FaMoneyBillWave className='text-emerald-500' /> Record Payment
              </h2>
              <button onClick={() => setShowCreate(false)} className='w-9 h-9 rounded-lg bg-slate-100 text-slate-600 flex items-center justify-center'>
                <FaTimes />
              </button>
            </div>
            <p className='text-sm text-slate-500 mb-5'>Log a subscription payment you have received.</p>

            <form onSubmit={handleCreate} className='space-y-4'>
              <div>
                <label className='block text-sm font-semibold text-slate-700 mb-1'>Account *</label>
                <GlassSelect
                  value={form.userId}
                  onChange={(e) => {
                    const acc = accountsById[e.target.value];
                    setForm({ ...form, userId: e.target.value, plan: acc?.subscription?.plan || form.plan });
                  }}
                >
                  <option value=''>— Select account —</option>
                  {accounts.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name} ({a.email})
                    </option>
                  ))}
                </GlassSelect>
                {selectedAccount && (
                  <p className='text-xs text-slate-400 mt-1'>
                    Current plan: {selectedAccount.subscription?.plan || '—'} · {selectedAccount.subscription?.status || '—'}
                  </p>
                )}
              </div>

              <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                <div>
                  <label className='block text-sm font-semibold text-slate-700 mb-1'>
                    <FaRupeeSign className='inline mr-1 text-slate-400' /> Amount *
                  </label>
                  <GlassInput
                    type='number'
                    min='0'
                    value={form.amount}
                    onChange={(e) => setForm({ ...form, amount: e.target.value })}
                    placeholder='e.g. 19999'
                  />
                </div>
                <div>
                  <label className='block text-sm font-semibold text-slate-700 mb-1'>Payment date</label>
                  <GlassInput type='date' value={form.paidAt} onChange={(e) => setForm({ ...form, paidAt: e.target.value })} />
                </div>
                <div>
                  <label className='block text-sm font-semibold text-slate-700 mb-1'>Plan</label>
                  <GlassSelect value={form.plan} onChange={(e) => setForm({ ...form, plan: e.target.value })}>
                    <option value=''>—</option>
                    {plans.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                  </GlassSelect>
                </div>
                <div>
                  <label className='block text-sm font-semibold text-slate-700 mb-1'>Method</label>
                  <GlassSelect value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value })}>
                    {METHODS.map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </GlassSelect>
                </div>
              </div>

              <div>
                <label className='block text-sm font-semibold text-slate-700 mb-1'>Note</label>
                <GlassInput
                  value={form.note}
                  onChange={(e) => setForm({ ...form, note: e.target.value })}
                  placeholder='e.g. Renewal for 2026-27'
                />
              </div>

              <div className='flex gap-3 pt-1'>
                <GlassButton type='submit' variant='gold' className='flex-1' disabled={saving}>
                  {saving ? 'Saving...' : 'Save Payment'}
                </GlassButton>
                <GlassButton type='button' variant='secondary' onClick={() => setShowCreate(false)}>
                  Cancel
                </GlassButton>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPaymentsPage;
